import debug from "debug";

const logger = debug("utility");

export function downloadFile(dataUrl: string, filename: string) {
	const link = document.createElement("a");
	link.href = dataUrl;
	link.download = filename;
	link.target = "_blank";
	link.click();
	link.remove();
}

export function loadScript(src: string): Promise<Event> {
	return new Promise<Event>((resolve, reject) => {
		logger("Loading script", src);
		const script = document.createElement("script");
		script.src = src;
		script.async = true;
		script.onload = resolve;
		script.onerror = reject;
		document.head.appendChild(script);
	});
}

export function randomString(length: number): string {
	const firstAlphabet = "CFGHJKLMNPRTVWXYZ";
	const alphabet = "1234567890CFGHJKLMNPRTVWXYZ";
	let result = firstAlphabet[Math.floor(Math.random() * firstAlphabet.length)];
	for (let i = 1; i < length; i++) {
		result += alphabet[Math.floor(Math.random() * alphabet.length)];
	}
	return result;
}

export function toParagraphs(text: string): string {
	return text.split("\n").map(p => `<p>${p}</p>`).join("");
}

export function numberFormat(n: number) {
	return n.toFixed(4);
}

export function arraySameContents<T>(a: T[], b: T[]): boolean {
	if (a.length !== b.length) {
		return false;
	}

	for (const item of a) {
		if (!b.includes(item)) {
			return false;
		}
	}

	return true;
}

export function arrayEquals<T>(a: T[], b: T[]): boolean {
	if (a.length !== b.length) {
		return false;
	}

	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}

	return true;
}

export function equalsWithEpsilon(a: number, b: number, epsilon: number): boolean {
	return Math.abs(a - b) < epsilon;
}

export function deepCopy<T>(obj: T): T {
	if (Array.isArray(obj)) {
		return obj.map((v) => deepCopy(v)) as T;
	}

	if (typeof obj !== "object") {
		return obj;
	}

	const copy: any = {};

	for (const key in obj) {
		copy[key] = deepCopy(obj[key]);
	}

	return copy;
}

export function deepEquals<T>(a: T, b?: T): boolean {
	if (a === b) {
		return true;
	}

	if (typeof a !== "object" || typeof b !== "object") {
		return false;
	}

	if (Array.isArray(a)) {
		if (!Array.isArray(b)) return false;

		return arrayEquals(a, b);
	} else if (Array.isArray(b)) {
		return false;
	}

	if (a === null) {
		return b === null;
	} else if (b === null) {
		return false;
	}

	for (const key in a) {
		if (!b.hasOwnProperty(key)) {
			return false;
		}

		if (!deepEquals(a[key], b[key])) {
			return false;
		}
	}

	return true;
}

/**
 * This function tries to apply the changes from source to target. The algorithm should try
 * to create as few changes as possible, it not only uses overwrite, but also shift and push
 * @param target the target array, which should be changed. (target is equal to source after this function)
 * @param source the source array, which should be applied to the target array
 * @param path a path which can be used for debugging purposes
 */
function applyArrayDiff<T>(target: T[], source: T[], path?: string) {
	path = path || "";

	const targetLength = target.length;
	const sourceLength = source.length;

	if (targetLength === 0 && sourceLength === 0) {
		return;
	}

	if (targetLength === 0) {
		logger(`Updating ${path} (empty -> ${source})`);
		target.push(...source);
		return;
	}

	if (sourceLength === 0) {
		logger(`Updating ${path} (${target} -> empty)`);
		target.splice(0, targetLength);
		return;
	}

	let i = 0;
	while (i < targetLength && i < sourceLength) {
		const targetV = target[i];
		const sourceV = source[i];

		const targetType = typeof targetV;
		const sourceType = typeof sourceV;

		if (targetType !== sourceType) {
			logger(`Updating ${path}[${i}] (${targetV} -> ${sourceV})`);
			target[i] = deepCopy(sourceV);
		} else {
			if (Array.isArray(target[i]) && Array.isArray(sourceV)) {
				applyArrayDiff(target[i] as any, sourceV, path + "[" + i + "]");
			} else if (targetType === "object") {
				applyDiff(target[i] as any, sourceV, path + "[" + i + "]");
			} else {
				if (targetV !== sourceV) {
					logger(`Updating ${path}[${i}] (${targetV} -> ${sourceV})`);
					target[i] = deepCopy(sourceV);
				}
			}
		}

		i++;
	}

	if (i < targetLength) {
		logger(`Deleting ${path}[${i}]`);
		target.splice(i, targetLength - i);
	} else if (i < sourceLength) {
		logger(`Adding ${path}[${i}] (${source[i]})`);
		target.push(deepCopy(source[i]));
		i++;
	}

	while (i < sourceLength) {
		logger(`Adding ${path}[${i}] (${source[i]})`);
		target.push(deepCopy(source[i]));
		i++;
	}
}

export function applyDiff<T>(target: T, source: T, path?: string) {
	path = path || "";

	if (target === undefined || source === undefined) {
		throw new Error("Undefined values are not allowed");
	} else if (typeof target !== "object" || typeof source !== "object") {
		throw new Error("Only objects are allowed");
	} else if (target === null || source === null) {
		throw new Error("Null values are not allowed");
	}

	for (const key in source) {
		if (!target.hasOwnProperty(key)) {
			logger(`Updating ${path}.${key} (${target[key]} -> ${source[key]})`);
			target[key] = source[key];
			continue;
		}
		const targetType = typeof target[key];
		const sourceType = typeof source[key];

		if (targetType !== sourceType) {
			logger(`Updating ${path}.${key} (${target[key]} -> ${source[key]})`);
			target[key] = deepCopy(source[key]);
		} else {
			if (Array.isArray(target[key]) && Array.isArray(source[key])) {
				applyArrayDiff(target[key] as any, source[key], path + "." + key);
			} else if (targetType === "object") {
				applyDiff(target[key] as any, source[key], path + "." + key);
			} else {
				if (target[key] !== source[key]) {
					logger(`Updating ${path}.${key} (${target[key]} -> ${source[key]})`);
					target[key] = deepCopy(source[key]);
				}
			}
		}
	}

	for (const key in target) {
		if (!(key in source)) {
			logger(`Deleting ${key}`);
			delete target[key];
		}
	}
}

/**
 * Requires all keys in an object to be not undefined
 *
 * @param obj the object to check
 */
export function requireAll<T>(obj: Partial<T>): Required<T> {
	for (const key in obj) {
		if (obj[key] === undefined) {
			throw new Error(`Missing required key: ${key}`);
		}
	}

	return obj as Required<T>;
}

export type PromptCsvInputKeys = {
	[key: string]: "string" | "number" | "boolean";
} | string[];

/**
 * Exports an array of objects to a CSV file
 *
 * @param filename the name of the file
 * @param objects the objects to export
 * @param keys the keys to include in the CSV, if not provided, all keys are included in the order of the first
 * 	      object's properties
 */
export function exportCsv(filename: string, objects: { [k: string]: any }[], keys?: string[]) {
	const csv = encodeCsv(objects, keys);
	const blob = new Blob([csv], { type: "text/csv", endings: "native" });
	const url = URL.createObjectURL(blob);
	downloadFile(url, filename);
	URL.revokeObjectURL(url);
}

export type CsvInputCharset = "utf-8" | "iso-8859";
export const CsvInputCharsets = ["utf-8", "iso-8859"] as CsvInputCharset[];
export type CsvInputSeparator = "," | ";" | "\t" | "|" | "auto";
export const CsvInputSeparators = [",", ";", "\t", "|", "auto"] as CsvInputSeparator[];
export const CsvInputSeparatorDescriptions: { [k in CsvInputSeparator]: string } = {
	auto: "Automatisch erkennen",
	"|": "Vertikaler Strich",
	",": "Komma",
	";": "Semikolon",
	"\t": "Tab-Zeichen"
};

export type CsvInputOptions = {
	keys?: PromptCsvInputKeys;
	separator?: string | "auto";
	charset?: CsvInputCharset;
}

export async function promptCsvInput<T>(options?: CsvInputOptions): Promise<[string, T[]]> {

	const keys = options?.keys;
	const separator = options?.separator === "auto" ? undefined : options?.separator;
	const charset = options?.charset ?? "utf-8";

	return new Promise((resolve, reject) => {
		const newFileObject = document.createElement("input");
		newFileObject.type = "file";
		newFileObject.accept = ".csv";

		newFileObject.onchange = () => {
			const files = newFileObject.files;
			if (files && files.length !== 1)
				return reject(new Error("Expected exactly one file"));

			const first = files!.item(0)!;
			const decodeAndResolve = (text: string) => {
				resolve([first.name, decodeCsv(text, keys, separator)]);
			};
			if (charset === "utf-8") {
				first.text().then(decodeAndResolve).catch(reject);
			} else if (charset === "iso-8859") {
				first.arrayBuffer().then((buffer) => {
					const decoder = new TextDecoder("iso-8859-1");
					const text = decoder.decode(buffer);
					decodeAndResolve(text);
				}).catch(reject);
			} else {
				reject(new Error(`Invalid charset: ${charset}`));
			}
		};
		newFileObject.click();
	});
}

export function detectCsvSeparator(text: string): string {
	const separators = [",", ";", "\t", "|"];
	const counts: { [seperator: string]: number } = { ",": 0, ";": 0, "\t": 0, "|": 0 };

	logger("Detecting CSV separator");

	for (const line of text.split("\n")) {
		for (const separator of separators) {
			counts[separator] += line.split(separator).length - 1;
		}
	}

	let max = 0;
	let maxSeparator = ",";

	for (const separator in counts) {
		if (counts[separator] > max) {
			max = counts[separator];
			maxSeparator = separator;
		}
	}

	logger(`Auto-detected separator: ${maxSeparator}`);
	return maxSeparator;
}

export function decodeCsvLine(line: string, separator: string = ","): string[] {

	const out: string[] = [];

	let state: "out" | "in" = "out";
	let currentValue = "";
	for (let i = 0; i < line.length; i++) {
		if (state === "in" && line[i] === "\"") {
			if (line.length >= i + 1 && line[i + 1] === "\"") {
				currentValue += "\"";
				i++;
			} else {
				state = "out";
			}
		} else if (state === "out" && line[i] === "\"") {
			state = "in";
		} else if (state === "out" && line[i] === separator) {
			out.push(currentValue);
			currentValue = "";
		} else {
			currentValue += line[i];
		}
	}

	// always (even when empty) add the last row to the output
	out.push(currentValue);

	return out;
}

export function decodeCsv<T>(text: string, keys?: PromptCsvInputKeys, separator?: string): T[] {

	if (!separator)
		separator = detectCsvSeparator(text);

	const out: T[] = [];
	const lines = text.split("\n").map(s => s.trim());

	if (lines.length === 0)
		return [];

	const headerLine = lines.shift()!;
	const headers = decodeCsvLine(headerLine.trim(), separator);

	if (!keys) {
		keys = headers;
	}

	for (let i = 0; i < lines.length; i++) {
		const line = decodeCsvLine(lines[i].trim(), separator);

		if (line.length === 0) {
			logger(`Skipping empty line ${i + 1}`);
			continue;
		}

		if (line.length === 1) {
			if (line[0].trim() === "") {
				logger(`Skipping empty line ${i + 1}`);
				continue;
			}
		}

		if (line.length !== headers.length) {
			logger(`Line length missmatch: (${line.length} vs ${headers.length})`);
			throw new Error(`Line ${i + 1} has a different number (${line.length}) of columns than the header (${headers.length})`);
		}

		const obj: any = {};

		for (let j = 0; j < headers.length; j++) {
			const value = line[j];
			const key = headers[j];
			const keyInfo = Array.isArray(keys) ? "string" : keys[key];

			if (keyInfo === undefined) {
				throw new Error(`Missing key info for key '${key}' (headers are: ${headers.join(",")}`);
			}

			if (keyInfo === "number") {
				obj[key] = parseFloat(value);
			} else if (keyInfo === "boolean") {
				obj[key] = value === "TRUE";
			} else if (keyInfo === "string") {
				obj[key] = value;
			} else {
				throw new Error(`Invalid key info for key ${key}`);
			}
		}

		out.push(obj as T);
	}

	return out;
}

/**
 * Encodes an array of objects into a CSV string
 *
 * @param objects the objects to encode
 * @param keys the keys to include in the CSV, if not provided, all keys are included in the order of the first
 * 	      object's properties
 */
export function encodeCsv(objects: { [k: string]: any }[], keys?: string[]) {

	logger("Encode CSV", objects);

	const headers = keys ?? Object.keys(objects[0]);

	function encodeCsvValue(v: any): string {
		if (v === undefined) {
			return "";
		} else if (typeof v === "string") {
			return `"${v.replace(/"/g, "\"\"")}"`;
		} else if (typeof v === "number") {
			return v.toString();
		} else if (typeof v === "boolean") {
			return v ? "TRUE" : "FALSE";
		} else {
			return encodeCsvValue(v.toString());
		}
	}

	const csv = [headers.map(encodeCsvValue).join(",")];

	for (const obj of objects) {
		const row = [];

		for (const key of headers) {
			if (obj.hasOwnProperty(key)) {
				row.push(encodeCsvValue(obj[key]));
			} else {
				row.push("");
			}
		}

		csv.push(row.join(","));
	}

	return csv.join("\n");
}